import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import { Error as ErrorIcon, Refresh } from "@mui/icons-material";

/**
 * ErrorBoundary - Error boundary component for quote link pages
 * Provides graceful error handling and recovery options
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error(
        "Quote Link Error Boundary caught an error:",
        error,
        errorInfo
      );
    }

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // In production, you might want to log this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            bgcolor: "grey.50",
          }}
        >
          <Card sx={{ maxWidth: 500, width: "100%" }}>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />

              <Typography variant="h5" gutterBottom>
                Something went wrong
              </Typography>

              <Typography variant="body1" color="text.secondary" paragraph>
                We encountered an unexpected error while loading your quote.
                This might be a temporary issue.
              </Typography>

              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="body2">
                  <strong>Error Details:</strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontFamily: "monospace", fontSize: "0.8rem" }}
                >
                  {this.state.error?.message || "Unknown error occurred"}
                </Typography>
              </Alert>

              <Box
                display="flex"
                gap={2}
                justifyContent="center"
                flexWrap="wrap"
              >
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>

                <Button variant="outlined" onClick={this.handleReload}>
                  Reload Page
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                If the problem persists, please contact our support team.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for handling errors in functional components
 */
export const useErrorHandler = () => {
  const handleError = (error, errorInfo = {}) => {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Quote Link Error:", error, errorInfo);
    }

    // In production, you might want to log this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  };

  return { handleError };
};

export default ErrorBoundary;

